/*eslint complexity: off*/
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import dig from 'object-dig';

import { ThemeProvider } from '@material-ui/styles';
import { makeStyles } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { createMuiTheme } from '@material-ui/core/styles';

import FaceIcon from '@material-ui/icons/Face';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';

import Chip from '@material-ui/core/Chip';
import Radio from '@material-ui/core/Radio';
import Input from '@material-ui/core/Input';
import Button from '@material-ui/core/Button';
import LinkMaterial from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '@material-ui/core/Typography';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';

import Paper from '~/components/Paper';
import GridItem from '~/components/GridItem';
import InputShow from '~/components/InputShow';
import GridContainer from '~/components/GridContainer';
import InputSelect from '~/components/Input/InputSelect';
import InputControl from '~/components/Input/InputControl';

import MedicalAppointmentsRecord from '~/records/MedicalAppointmentsRecord';

import Form from '~/containers/Form';
import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import { showPerson } from '~/config/routes';
import useStyles from './styles';
import useCamera from '../hooks/useCamera';
import AgoraRTC from '../utils/AgoraEnhancer';
import useMicrophone from '../hooks/useMicrophone';
import { applyVirtualBackground } from '../utils/virtualBackground';
import Activities from '~/components/Activities';
import AttendantWebSocket from '~/contexts/AttendantWebSocket';
import { appName } from '../../../config/uri';
import AppointmentScheduler from '~/components/AppointmentScheduler';
import { parse } from 'date-fns';
import moment from 'moment';

const attrName = (attr) => MedicalAppointmentsRecord.hummanAttributeName(attr);

const videoStyles = makeStyles(() => ({
  buttonItem: {
    width: '100%',
  },
  advanceSettings: {
    marginTop: 0,
  },
}));

const CLOSED_STATUSES = ['no_show', 'canceled'];
const CANCELABLE_STATUSES = [
  'new',
  'on_schedule',
  'wait_room',
  'wait_room_scheduled',
  'pre_appointment',
  'ongoing',
  'in_consultation',
  'canceled',
];
const NO_SHOW_BY = ['patient', 'doctor'];
const CANCEL_REASONS = [
  'rescheduled',
  'technical_issue',
  'patient_request',
  'scheduled_by_mistake',
];

function ClosureOptions({ attribute, options, value, onChange }) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.closureOptions}>
      <FormLabel required focused={false} className={classes.closureLabel}>
        {attrName(attribute)}
      </FormLabel>
      <RadioGroup
        name={attribute}
        value={value || ''}
        onChange={(event) => onChange({ [attribute]: event.target.value })}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio color="primary" size="small" />}
            label={t(`medicalAppointments.${attribute}.${option}`)}
            classes={{ label: classes.closureOptionLabel }}
          />
        ))}
      </RadioGroup>
    </div>
  );
}

ClosureOptions.propTypes = {
  attribute: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

const extractReason = (notes) => {
  const match = String(notes || '').match(
    /Motivo da consulta:\s*([\s\S]*?)(?:\n\s*\n\s*Agendado por|$)/
  );
  return match ? match[1].trim() : '';
};

function MedicalAppointmentsItem({
  record,
  handleChange,
  onSubmit,
  loading,
  user,
  setRecord,
  userProfile,
  loadedStatus,
}) {
  const classesRoot = useStyles();
  const classes = videoStyles();
  const reason = extractReason(record.get('notes'));
  const [ongoingCall, setOngoingCall] = useState(false);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [doctorsOptions, setDoctorsOptions] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [specialtiesOptions, setSpecialtiesiesOptions] = useState([]);
  const [attendantOptions, setAttendantsOptions] = useState([]);
  const [appId, setappId] = useState('');
  const [channel, setChannel] = useState('');
  const [uid] = useState(null); // null = Agora gera UID automaticamente
  const [token, setToken] = useState(null);
  const [cameraId, setCameraId] = useState('');
  const [microphoneId, setMicrophoneId] = useState('');
  const [mode] = useState('live');
  const [codec] = useState('h264');
  const [agoraClient, setClient] = useState(undefined);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState({});
  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const { attendant, webSocket } = useContext(AttendantWebSocket);
  const [modalOpen, setModalOpen] = useState(false);
  const [scheduleDates, setScheduleDates] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [specialityState, setSpecialityState] = useState(null);
  const [scheduleDateFormatted, setScheduleDateFormatted] = useState('');
  const [specialityInterval, setSpecialityInterval] = useState(0);
  const [changeSpecialty, setChangeSpecialty] = useState(false);

  const handleRadio = (event) => handleChange({ priority: event.target.value });
  const handleStatus = (event) => handleChange({ status: event.target.value });
  const handleModalSchedule = () => setModalOpen(!modalOpen);
  const handleSpeciality = (spec) => {
    setChangeSpecialty(true);
    setRecord(record.updateAttributes({ specialty: spec }));
    setSpecialityState(spec.id);
  };
  const { t } = useTranslation();
  const isPersisted = record.isPersisted();
  const closed = CLOSED_STATUSES.includes(loadedStatus);
  const cameraList = useCamera();
  const microphoneList = useMicrophone();
  async function fetchDoctors(idSpec) {
    if (idSpec) {
      Request.getNotDeserialize(
        `/admin/schedules/doctors_by_specialty?specialty_id=${idSpec}`,
        null,
        { normalizeJSON: true }
      )
        .then((response) => {
          const items = response.data.map((user) => {
            return {
              id: Number(user.user_id),
              name: `${user.user_name}${
                isPresent(user.specialty_name)
                  ? ` <${user.specialty_name}>`
                  : ' <Clínico Geral>'
              }`,
            };
          });
          const data = [
            ...items,
            { id: null, name: t('medicalAppointments.extras.no_doctor') },
          ];
          setDoctorsOptions(data);
          if (changeSpecialty) {
            setRecord(record.updateAttributes({ doctor: null }));
          }

          if (record.get('status') === 'ongoing') {
            setOngoingCall(true);
          }
        })
        .catch((e) => {
          console.log(e);
          setDoctorsOptions([]);
        });
    }
  }
  async function fetchAttendants() {
    Request.get('/admin/users_attendants').then((result) => {
      const data = [
        ...result.data,
        { id: null, name: t('medicalAppointments.extras.no_attendant') },
      ];
      setAttendantsOptions(data);
    });
  }

  async function fetchCategories() {
    Request.get('/admin/appointment_categories', null, { normalizeJSON: true })
      .then((response) => {
        setCategoriesOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setCategoriesOptions([]);
      });
  }

  async function fetchSpecialties() {
    Request.get('/admin/specialties', null, { normalizeJSON: true })
      .then((response) => {
        setSpecialtiesiesOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setSpecialtiesiesOptions([]);
      });
  }

  const fetchAgoraAppId = async () => {
    if (record && record.get('id')) {
      const data = await Request.get(`/admin/agora/${record.get('id')}`, null, {
        normalizeJSON: false,
      });
      setappId(data.app_id);
      setChannel(data.channel_id);
      setToken(data.token);
    }
  };

  async function checkScheduleGreaterCurrentTime(
    schedule,
    selectedDay,
    specialtyIntervalInMinutes,
    currentTime,
    isToday
  ) {
    if (schedule.enabled) {
      const startSchedule = `${selectedDay}T${schedule.value}:00-0300`;
      let scheduleAfterInterval = moment(startSchedule)
        .add(specialtyIntervalInMinutes, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss')
        .toString();
      scheduleAfterInterval = `${scheduleAfterInterval}-0300`;

      if (isToday) {
        const isGreaterCurrentTime = moment(currentTime).isBefore(
          scheduleAfterInterval
        );
        if (isGreaterCurrentTime) {
          return {
            startTime: new Date(startSchedule),
            endTime: new Date(scheduleAfterInterval),
          };
        }
      } else {
        return {
          startTime: new Date(startSchedule),
          endTime: new Date(scheduleAfterInterval),
        };
      }
    }
  }

  async function getAndPopulateSchedule(
    dataMonth,
    specialtyIntervalInMinutes,
    nextMonth
  ) {
    const list = await dataMonth.data;
    const availableTimeSlots = [];
    const currentTime = moment().format();

    for await (const day of list) {
      const daySchedules = [];

      if (day.enabled) {
        const selectedDay = day.value;
        const isToday =
          moment().format('L') === moment(selectedDay).format('L')
            ? true
            : false;
        const isGreaterToday =
          moment().format('L') < moment(selectedDay).format('L') ? true : false;

        if (isGreaterToday || isToday || nextMonth) {
          for await (const schedule of day.hours) {
            if (schedule.enabled) {
              const dayList = await checkScheduleGreaterCurrentTime(
                schedule,
                selectedDay,
                specialtyIntervalInMinutes,
                currentTime,
                isToday
              );
              daySchedules.push(dayList);
            }
          }
          const availableObject = {
            date: day.value,
            schedules: daySchedules,
          };

          availableTimeSlots.push(availableObject);
        }
      }
    }
    setLoadingSchedule(false);
    return availableTimeSlots;
  }

  const fetchSchedule = async (doctorId) => {
    const specialtyId = record.get('specialty')
      ? record.get('specialty').id
      : specialityState;
    if (
      !loadingSchedule &&
      specialtyId &&
      specialtyId.length > 0 &&
      specialtiesOptions.length > 0 &&
      doctorId
    ) {
      setLoadingSchedule(true);
      const specialityFilter = specialtiesOptions.filter(
        (item) => item.id === specialtyId
      );
      const specialityInterval = Number(
        specialityFilter[0].appointment_interval
      );
      const specialtyDuration = Number(
        specialityFilter[0].appointment_duration
      );
      const specialtyIntervalInMinutes = specialityInterval + specialtyDuration;
      setSpecialityInterval(specialtyIntervalInMinutes);
      const monthYear = moment(new Date()).format('yyyy-MM');
      const urlRequest = '/admin/schedules/available_hours';
      const nextMonth = moment(new Date()).add(1, 'month').format('yyyy-MM');

      const getMonthData = await Request.getNotDeserialize(urlRequest, {
        year_month: monthYear,
        specialty_id: specialtyId,
        doctor_id: doctorId,
      });
      const nextMonthData = await Request.getNotDeserialize(urlRequest, {
        year_month: nextMonth,
        specialty_id: specialtyId,
        doctor_id: doctorId,
      });

      Promise.all([getMonthData, nextMonthData]).then(async (resp) => {
        const dataMonth = await getAndPopulateSchedule(
          { data: resp[0].data },
          specialtyIntervalInMinutes,
          false
        );
        const dataNextMonth = await getAndPopulateSchedule(
          { data: resp[1].data },
          specialtyIntervalInMinutes,
          true
        );

        Promise.all([dataMonth, dataNextMonth]).then((response) => {
          const data = response[0].concat(response[1]);
          setScheduleDates(data);
        });
      });
    }
  };

  // Cria os tracks locais de áudio e vídeo. É AQUI que o navegador exibe o
  // prompt de permissão de câmera/microfone (getUserMedia), por isso esta
  // chamada precisa acontecer ANTES de entrar no canal: assim a permissão é
  // solicitada imediatamente ao clicar em "Ligar", independente do join.
  const acquireLocalTracks = async () => {
    // Contexto seguro: sem HTTPS (ou localhost) `navigator.mediaDevices` é
    // undefined e o navegador nem chega a pedir permissão de câmera.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const err = new Error('mediaDevices indisponível (contexto não seguro)');
      err.name = 'MediaNotSupportedError';
      throw err;
    }

    // SDK v4: Cria tracks de áudio e vídeo
    const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks(
      {
        // Configurações de áudio
        AEC: true, // Cancelamento de eco
        ANS: true, // Supressão de ruído
      },
      {
        // Configurações de vídeo - encoderConfig substitui setVideoProfile
        encoderConfig: {
          width: 1920,
          height: 1080,
          frameRate: 30,
          bitrateMin: 1000,
          bitrateMax: 3000,
        },
        // Aplica deviceId se foi selecionado
        cameraId: cameraId || undefined,
      }
    );

    // Aplica deviceId do microfone se foi selecionado
    if (microphoneId && audioTrack) {
      await audioTrack.setDevice(microphoneId);
    }

    return [audioTrack, videoTrack];
  };

  // Traduz a falha de getUserMedia em uma mensagem clara para o operador.
  const handleMediaError = (err) => {
    const name = err && err.name ? err.name : '';
    let key = 'medicalAppointments.extras.media_error_generic';

    if (name === 'MediaNotSupportedError') {
      key = 'medicalAppointments.extras.media_not_supported';
    } else if (['NotAllowedError', 'PermissionDeniedError'].includes(name)) {
      key = 'medicalAppointments.extras.media_permission_denied';
    } else if (['NotFoundError', 'DevicesNotFoundError'].includes(name)) {
      key = 'medicalAppointments.extras.media_not_found';
    } else if (['NotReadableError', 'TrackStartError'].includes(name)) {
      key = 'medicalAppointments.extras.media_in_use';
    } else if (
      ['OverconstrainedError', 'ConstraintNotSatisfiedError'].includes(name)
    ) {
      key = 'medicalAppointments.extras.media_overconstrained';
    }

    toast.error(t(key));

    Request.post('/public/sendError', {
      title: 'ADMIN: ERRO DE PERMISSÃO DE MÍDIA',
      message: err && err.message,
      stack: err && err.stack,
      record: record,
    })
      .then()
      .catch(() => {});
  };

  // Fecha tracks com segurança (usado quando o join falha após adquiri-los).
  const closeTracks = (audioTrack, videoTrack) => {
    [audioTrack, videoTrack].forEach((track) => {
      if (track) {
        try {
          track.stop();
          track.close();
        } catch (e) {
          /* noop */
        }
      }
    });
  };

  const join = async () => {
    setIsLoading(true);

    // 1) Adquire câmera/microfone PRIMEIRO — dispara o prompt de permissão
    // logo no clique, antes de qualquer chamada de rede ou entrada no canal.
    let audioTrack;
    let videoTrack;
    try {
      [audioTrack, videoTrack] = await acquireLocalTracks();
    } catch (err) {
      setIsLoading(false);
      handleMediaError(err);
      return;
    }

    // 2) Com a permissão concedida, entra no canal e publica.
    try {
      const status = record.get('status');
      if (status !== 'ongoing') {
        await Request.put(`/admin/init_appointment/${record.get('id')}`);
      }

      // SDK v4: Creates a new agora client with given parameters.
      // mode can be 'rtc' for real time communications or 'live' for live broadcasting.
      const client = AgoraRTC.createClient({ mode: mode, codec: codec });
      setClient(client);

      // SDK v4: join recebe appId, channel, token, uid (ordem diferente do v3)
      await client.join(appId, channel, token, uid);

      // Define role como host para poder publicar (necessário em modo 'live')
      if (mode === 'live') {
        await client.setClientRole('host');
      }

      // Salva os tracks no state
      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);

      // Fundo virtual padrao (tela branca + logo) para o operador do admin.
      await applyVirtualBackground(videoTrack);

      // SDK v4: Publish aceita array de tracks
      await client.publish([audioTrack, videoTrack]);

      // Set the state appropriately
      setIsJoined(true);

      if (userProfile && userProfile.role === 'doctor') {
        handleChange({ status: 'in_consultation' });
      } else {
        handleChange({ status: 'pre_appointment' });
      }
      toast.success(t('medicalAppointments.extras.joined_channel_success'));
    } catch (err) {
      // Falha após adquirir os tracks: libera a câmera/microfone.
      closeTracks(audioTrack, videoTrack);
      sendError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const leave = async () => {
    const data = {
      schedule_at: record.get('schedule_appointment_at')
        ? record.get('schedule_appointment_at')
        : null,
      doctor_id: dig(record.get('doctor'), 'id') || null,
    };

    Request.put(`/admin/end_appointment/${record.get('id')}`, data);

    setIsLoading(true);
    try {
      // SDK v4: Close tracks individualmente
      if (localAudioTrack) {
        localAudioTrack.stop();
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.stop();
        localVideoTrack.close();
      }

      // SDK v4: Unpublish tracks
      const tracksToUnpublish = [];
      if (localAudioTrack) tracksToUnpublish.push(localAudioTrack);
      if (localVideoTrack) tracksToUnpublish.push(localVideoTrack);

      if (tracksToUnpublish.length > 0) {
        await agoraClient.unpublish(tracksToUnpublish);
      }

      // leave the channel
      await agoraClient.leave();

      // Limpa os states
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setIsJoined(false);

      if (userProfile && userProfile.role === 'doctor') {
        handleChange({ status: 'finished' });
      } else {
        handleChange({ status: 'pre_appointment_finished' });
      }
      toast.success(t('medicalAppointments.extras.left_channel_success'));
    } catch (err) {
      sendError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const sendError = async (err) => {
    toast.error(
      `${t('medicalAppointments.extras.joined_channel_fail')} ***** ${
        err.message
      }`
    );
    Request.post('/public/sendError', {
      title: 'ADMIN: ERRO NA VIDEO CHAMADA',
      message: err.message,
      stack: err.stack,
      record: record,
    }).then();
  };

  const restart = async () => {
    setIsLoading(true);
    try {
      // SDK v4: Close tracks individualmente
      if (localAudioTrack) {
        localAudioTrack.stop();
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.stop();
        localVideoTrack.close();
      }

      // SDK v4: Unpublish tracks
      const tracksToUnpublish = [];
      if (localAudioTrack) tracksToUnpublish.push(localAudioTrack);
      if (localVideoTrack) tracksToUnpublish.push(localVideoTrack);

      if (tracksToUnpublish.length > 0) {
        await agoraClient.unpublish(tracksToUnpublish);
      }

      // leave the channel
      await agoraClient.leave();
      setIsJoined(false);
      toast.success(t('medicalAppointments.extras.left_channel_success'));
    } catch (err) {
      sendError(err);
    } finally {
      setIsLoading(false);
    }

    // Readquire câmera/microfone ANTES de reentrar no canal (mesmo motivo do
    // join: garante o prompt de permissão e falha cedo com mensagem clara).
    setIsLoading(true);
    let audioTrack;
    let videoTrack;
    try {
      [audioTrack, videoTrack] = await acquireLocalTracks();
    } catch (err) {
      setIsLoading(false);
      handleMediaError(err);
      return;
    }

    // Recria o client
    const client = AgoraRTC.createClient({ mode: mode, codec: codec });
    setClient(client);

    try {
      // SDK v4: join recebe appId, channel, token, uid
      await client.join(appId, channel, token, uid);

      // Define role como host para poder publicar (necessário em modo 'live')
      if (mode === 'live') {
        await client.setClientRole('host');
      }

      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);

      // Fundo virtual padrao (tela branca + logo) para o operador do admin.
      await applyVirtualBackground(videoTrack);

      // SDK v4: Publish tracks
      await client.publish([audioTrack, videoTrack]);

      setIsJoined(true);
      toast.success(t('medicalAppointments.extras.joined_channel_success'));
    } catch (err) {
      closeTracks(audioTrack, videoTrack);
      sendError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // const callDoctor = () => {
  //   Request.put(`/admin/call_doctor/${record.get('id')}/${record.get('doctor_id')}`);
  // };

  const finishCall = () => {
    if (localAudioTrack || localVideoTrack) {
      leave();
    }
  };

  useEffect(() => {
    if (attendant && webSocket) {
      webSocket.on('message', ({ type, payload }) => {
        if (type === 'permissions' && payload) {
          setCameraBlocked(!payload.camera);
          setAudioBlocked(!payload.audio);
        }
      });
    }
  }, [attendant, webSocket]);

  useEffect(() => {
    fetchAgoraAppId();
    fetchCategories();
    fetchAttendants();
  }, [record.get('doctor'), record.get('uuid'), agoraClient]);

  useEffect(() => {
    if (!agoraClient) return;

    // SDK v4: Evento quando usuário publica mídia (áudio ou vídeo)
    const handleUserPublished = async (user, mediaType) => {
      try {
        // Subscribe ao usuário
        await agoraClient.subscribe(user, mediaType);

        if (mediaType === 'video') {
          // Adiciona/atualiza usuário remoto com video track
          setRemoteUsers((prevUsers) => ({
            ...prevUsers,
            [user.uid]: {
              ...prevUsers[user.uid],
              uid: user.uid,
              videoTrack: user.videoTrack,
            },
          }));
        }

        if (mediaType === 'audio') {
          // Adiciona/atualiza usuário remoto com audio track
          setRemoteUsers((prevUsers) => ({
            ...prevUsers,
            [user.uid]: {
              ...prevUsers[user.uid],
              uid: user.uid,
              audioTrack: user.audioTrack,
            },
          }));
          // Audio track deve ser reproduzido automaticamente
          if (user.audioTrack) {
            user.audioTrack.play();
          }
        }
      } catch (error) {
        console.error('Error subscribing to user:', error);
      }
    };

    // SDK v4: Evento quando usuário cancela publicação de mídia
    const handleUserUnpublished = (user, mediaType) => {
      if (mediaType === 'video') {
        setRemoteUsers((prevUsers) => {
          const updatedUsers = { ...prevUsers };
          if (updatedUsers[user.uid]) {
            updatedUsers[user.uid].videoTrack = null;
          }
          return updatedUsers;
        });
      }

      if (mediaType === 'audio') {
        setRemoteUsers((prevUsers) => {
          const updatedUsers = { ...prevUsers };
          if (updatedUsers[user.uid]) {
            updatedUsers[user.uid].audioTrack = null;
          }
          return updatedUsers;
        });
      }
    };

    // SDK v4: Evento quando usuário sai do canal
    const handleUserLeft = (user) => {
      setRemoteUsers((prevUsers) => {
        const updatedUsers = { ...prevUsers };
        delete updatedUsers[user.uid];
        return updatedUsers;
      });
    };

    // Registra os event listeners
    agoraClient.on('user-published', handleUserPublished);
    agoraClient.on('user-unpublished', handleUserUnpublished);
    agoraClient.on('user-left', handleUserLeft);

    return () => {
      // Remove os event listeners
      agoraClient.off('user-published', handleUserPublished);
      agoraClient.off('user-unpublished', handleUserUnpublished);
      agoraClient.off('user-left', handleUserLeft);
    };
  }, [agoraClient]);

  // SDK v4: Reproduz o vídeo local quando o track estiver disponível
  useEffect(() => {
    if (localVideoTrack && localVideoRef.current) {
      localVideoTrack.play(localVideoRef.current);
    }

    return () => {
      if (localVideoTrack) {
        localVideoTrack.stop();
      }
    };
  }, [localVideoTrack]);

  // O agendamento (especialidade/medico/horario) foi extraido para
  // <AppointmentScheduler>. Aqui so mantemos a opcao de status "ongoing"
  // disponivel quando a consulta esta em andamento.
  useEffect(() => {
    if (record.get('status') === 'ongoing') setOngoingCall(true);
  }, [record.get('status')]);

  // SDK v4: Cleanup quando componente desmonta
  useEffect(() => {
    return () => {
      if (isJoined && (localAudioTrack || localVideoTrack) && agoraClient) {
        try {
          // Close tracks diretamente
          if (localAudioTrack) {
            localAudioTrack.stop();
            localAudioTrack.close();
          }
          if (localVideoTrack) {
            localVideoTrack.stop();
            localVideoTrack.close();
          }

          // Unpublish e leave
          if (agoraClient) {
            const tracksToUnpublish = [];
            if (localAudioTrack) tracksToUnpublish.push(localAudioTrack);
            if (localVideoTrack) tracksToUnpublish.push(localVideoTrack);

            if (tracksToUnpublish.length > 0) {
              agoraClient.unpublish(tracksToUnpublish).catch((e) =>
                console.error('Error unpublishing:', e)
              );
            }

            agoraClient
              .leave()
              .then(() => {
                const data = {
                  schedule_at: record.get('schedule_appointment_at')
                    ? record.get('schedule_appointment_at')
                    : null,
                  doctor_id: dig(record.get('doctor'), 'id') || null,
                };

                Request.put(`/admin/end_appointment/${record.get('id')}`, data);
              })
              .catch((e) => console.error('Error leaving channel:', e));
          }
        } catch (err) {
          console.error('Error cleaning up tracks:', err);
        }
      }
    };
  }, [isJoined, localAudioTrack, localVideoTrack, agoraClient]);

  return (
    <>
      <Form
        onSubmit={() => onSubmit(finishCall)}
        isPersisted={isPersisted}
        isStreamActive={!!(localAudioTrack || localVideoTrack)}
      >
        <Paper
          Icon={LocalHospitalIcon}
          iconColor={isPersisted ? 'purple' : 'blue'}
          title={t('medicalAppointments.defSingular')}
          loading={loading}
        >
          <div className={clsx(classesRoot.root)}>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <InputLabel shrink>{attrName('user')}</InputLabel>
                <Chip
                  icon={<FaceIcon />}
                  label={user.name}
                  component={LinkMaterial}
                  href={showPerson.reverse({ id: user.id })}
                  style={{ cursor: 'pointer' }}
                  target="_blank"
                  color="primary"
                  variant="outlined"
                  onDelete={() => {}}
                  deleteIcon={<OpenInNewIcon />}
                />
              </GridItem>
              <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
                <InputShow
                  label={attrName('user_phone')}
                  value={user.cell_phone}
                />
              </GridItem>
              <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
                <InputShow label={attrName('email')} value={user.email} />
              </GridItem>
            </GridContainer>


            <GridContainer>
              <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
                <InputControl
                  name={'attendant'}
                  value={record.get('attendant')}
                  label={attrName('attendant')}
                  inputComponent={InputSelect}
                  onChange={({ attendant }) => {
                    setRecord(
                      record.updateAttributes({ attendant: attendant })
                    );
                  }}
                  InputProps={{
                    isClearable: true,
                    options: attendantOptions,
                    outValueType: 'object',
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
                <InputControl
                  name={'category'}
                  value={record.get('category')}
                  label={attrName('category')}
                  inputComponent={InputSelect}
                  onChange={({ category }) => {
                    setRecord(record.updateAttributes({ category: category }));
                  }}
                  InputProps={{
                    isClearable: true,
                    options: categoriesOptions,
                    outValueType: 'object',
                  }}
                />
              </GridItem>
            </GridContainer>
            <AppointmentScheduler
              record={record}
              setRecord={setRecord}
              disabled={closed}
            />
            <br />
            <br />
            <GridContainer>
              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <FormLabel component="legend">{attrName('priority')}</FormLabel>
                <RadioGroup
                  aria-label="position"
                  name="position"
                  value={record.get('priority')}
                  onChange={handleRadio}
                >
                  <FormControlLabel
                    value="low"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.priority.priority_low')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="medium"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.priority.priority_medium')}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="high"
                    control={<Radio color="primary" />}
                    label={t('medicalAppointments.priority.priority_high')}
                    labelPlacement="end"
                  />
                </RadioGroup>
              </GridItem>

              <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
                <FormControl component="fieldset" disabled={closed}>
                  <FormLabel component="legend" focused={false}>
                    {attrName('status')}
                  </FormLabel>
                  <RadioGroup
                    aria-label="position"
                    name="position"
                    value={record.get('status')}
                    onChange={handleStatus}
                  >
                    <FormControlLabel
                      value="on_approval"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.on_approval')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="new"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.new')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="pre_appointment"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.pre_appointment')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="pre_appointment_finished"
                      control={<Radio color="primary" />}
                      label={t(
                        'medicalAppointments.status.pre_appointment_finished'
                      )}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="on_schedule"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.on_schedule')}
                      labelPlacement="end"
                    />
                    {ongoingCall && (
                      <FormControlLabel
                        value="ongoing"
                        control={<Radio color="primary" />}
                        label={t('medicalAppointments.status.ongoing')}
                        labelPlacement="end"
                      />
                    )}
                    <FormControlLabel
                      value="wait_room"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.wait_room')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="in_consultation"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.in_consultation')}
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="no_show"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.no_show')}
                      labelPlacement="end"
                    />
                    {record.get('status') === 'no_show' && (
                      <ClosureOptions
                        attribute="no_show_by"
                        options={NO_SHOW_BY}
                        value={record.get('no_show_by')}
                        onChange={handleChange}
                      />
                    )}
                    {CANCELABLE_STATUSES.includes(loadedStatus) && (
                      <FormControlLabel
                        value="canceled"
                        control={<Radio color="primary" />}
                        label={t('medicalAppointments.status.canceled')}
                        labelPlacement="end"
                      />
                    )}
                    {record.get('status') === 'canceled' && (
                      <ClosureOptions
                        attribute="cancel_reason"
                        options={CANCEL_REASONS}
                        value={record.get('cancel_reason')}
                        onChange={handleChange}
                      />
                    )}
                    <FormControlLabel
                      value="finished"
                      control={<Radio color="primary" />}
                      label={t('medicalAppointments.status.finished')}
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </FormControl>
              </GridItem>
            </GridContainer>

            {/* <GridItem xs={4} sm={4} md={4}> */}
            {/* <Button */}
            {/*   className={classes.buttonItem} */}
            {/*   color={'primary'} */}
            {/*   onClick={callDoctor} */}
            {/*   variant="contained" */}
            {/*   disabled={isLoading} */}
            {/* > */}
            {/*   {'Chamar o médico para o atendimento'} */}
            {/* </Button> */}
            {/* </GridItem> */}

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <InputControl
                  value={record.get('notes')}
                  name={'notes'}
                  label={attrName('notes')}
                  inputComponent={Input}
                  InputProps={{
                    multiline: true,
                    rows: '6',
                  }}
                  onChange={handleChange}
                />
              </GridItem>
            </GridContainer>

            <GridContainer>
              <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
                <InputControl
                  value={reason}
                  name={'reason'}
                  label={attrName('reason')}
                  inputComponent={Input}
                  InputProps={{
                    multiline: true,
                    rows: '4',
                    readOnly: true,
                  }}
                  onChange={() => {}}
                />
              </GridItem>
            </GridContainer>
            <GridItem xs={12}>
              <GridContainer>
                <GridItem item xs={6} md={6}>
                  <ThemeProvider
                    theme={createMuiTheme({
                      palette: {
                        type: 'dark',
                      },
                    })}
                  >
                    <ExpansionPanel className={classes.advanceSettings}>
                      <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>
                          {t('medicalAppointments.extras.configs')}
                        </Typography>
                      </ExpansionPanelSummary>
                      <ExpansionPanelDetails>
                        <form noValidate autoComplete="off">
                          <TextField
                            id="cameraId"
                            value={cameraId}
                            onChange={(cameraId) =>
                              setCameraId(cameraId.target.value)
                            }
                            select
                            label={t('medicalAppointments.extras.cam')}
                            helperText={t(
                              'medicalAppointments.extras.select_cam'
                            )}
                            fullWidth
                            margin="normal"
                          >
                            {cameraList.map((item) => (
                              <MenuItem
                                key={item.deviceId}
                                value={item.deviceId}
                              >
                                {item.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            id="microphoneId"
                            value={microphoneId}
                            onChange={(microphoneId) =>
                              setMicrophoneId(microphoneId.target.value)
                            }
                            select
                            label={t('medicalAppointments.extras.microphone')}
                            helperText={t(
                              'medicalAppointments.extras.select_microphone'
                            )}
                            fullWidth
                            margin="normal"
                          >
                            {microphoneList.map((item) => (
                              <MenuItem
                                key={item.deviceId}
                                value={item.deviceId}
                              >
                                {item.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </form>
                      </ExpansionPanelDetails>
                    </ExpansionPanel>
                  </ThemeProvider>
                </GridItem>
                <GridItem item xs={3} md={3}>
                  <Button
                    className={classes.buttonItem}
                    color={isJoined ? 'secondary' : 'primary'}
                    onClick={isJoined ? leave : join}
                    variant="contained"
                    disabled={isLoading || (closed && !isJoined)}
                  >
                    {isJoined
                      ? t('medicalAppointments.extras.finish_call')
                      : t('medicalAppointments.extras.init_call')}
                  </Button>
                </GridItem>
                <GridItem item xs={3} md={3}>
                  {isJoined && (
                    <Button
                      className={classes.buttonItem}
                      color={'secondary'}
                      onClick={restart}
                      variant="contained"
                      disabled={isLoading}
                    >
                      {t('medicalAppointments.extras.refresh')}
                    </Button>
                  )}
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem item xs={6} md={6}>
                  {/* SDK v4: Video local usando ref */}
                  {localVideoTrack && (
                    <div
                      ref={localVideoRef}
                      style={{
                        width: '100%',
                        height: '400px',
                        backgroundColor: '#000',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '10px',
                          left: '10px',
                          color: 'white',
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          padding: '5px 10px',
                          borderRadius: '4px',
                        }}
                      >
                        {appName}
                      </div>
                    </div>
                  )}
                </GridItem>

                <GridItem item xs={6} md={6}>
                  {/* SDK v4: Videos remotos usando refs dinâmicas */}
                  {Object.values(remoteUsers).map((remoteUser) => (
                    remoteUser.videoTrack && (
                      <div
                        key={remoteUser.uid}
                        ref={(el) => {
                          if (el && remoteUser.videoTrack) {
                            remoteUser.videoTrack.play(el);
                          }
                        }}
                        style={{
                          width: '100%',
                          height: '400px',
                          backgroundColor: '#000',
                          position: 'relative',
                          marginBottom: '10px',
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '10px',
                            left: '10px',
                            color: 'white',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            padding: '5px 10px',
                            borderRadius: '4px',
                          }}
                        >
                          {user.name || `User ${remoteUser.uid}`}
                        </div>
                      </div>
                    )
                  ))}
                </GridItem>
              </GridContainer>
            </GridItem>

            <GridContainer style={{ marginBottom: 40 }}>
              <GridItem xs={12}>{t('permissions_user')}</GridItem>
              {cameraBlocked && (
                <GridItem xs={12}>
                  <b>{t('camera_blocked')}</b>
                </GridItem>
              )}
              {audioBlocked && (
                <GridItem xs={12}>
                  <b>{t('audio_blocked')}</b>
                </GridItem>
              )}
            </GridContainer>

            <Activities
              data={record.get('activities')}
              keys={['user_name', 'info', 'platform', 'created_at']}
            />
          </div>
        </Paper>
      </Form>
    </>
  );
}

MedicalAppointmentsItem.propTypes = {
  record: PropTypes.instanceOf(MedicalAppointmentsRecord).isRequired,
  handleChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  setRecord: PropTypes.func.isRequired,
  loadedStatus: PropTypes.string,
};

MedicalAppointmentsItem.defaultProps = {
  loading: false,
  loadedStatus: '',
};

export default React.memo(withRouter(MedicalAppointmentsItem));
